import React, { useState } from 'react';
import { Image, Tag, X, Crop, Upload } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { ImageCrop } from '../../schemas/validation';
import { ImageCropModal } from '../../components/ComponentEditModal/shared/ImageCropModal';
import type { CropData } from '../../components/ComponentEditModal/shared/ImageCropModal';
import type { Area } from 'react-easy-crop';
import { getCroppedImageRenderProps } from '../../components/ComponentEditModal/shared/cropUtils';
import { ImageUrlWithUpload } from '../../components/ImageUrlWithUpload';
import { MediaUploadModal } from '../../components/MediaUploadModal';

interface BlogPostEditorSidebarProps {
  featuredImageUrl: string;
  featuredImageCrop?: ImageCrop;
  authorName: string;
  slug: string;
  postStatus?: 'draft' | 'published';
  tags: string[];
  newTag: string;
  seoTitle: string;
  seoDescription: string;
  seoImageUrl: string;
  title: string;
  excerpt: string;
  showSeoPanel: boolean;
  isUploadingImage: boolean;
  /** Site ID for media library access */
  siteId?: string;
  onFeaturedImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFeaturedImageUrlChange: (url: string) => void;
  onRemoveFeaturedImage: () => void;
  onFeaturedImageCropChange: (crop: CropData | undefined) => void;
  onAuthorNameChange: (value: string) => void;
  onSlugChange: (value: string) => void;
  onNewTagChange: (value: string) => void;
  onAddTag: () => void;
  onRemoveTag: (tag: string) => void;
  onSeoTitleChange: (value: string) => void;
  onSeoDescriptionChange: (value: string) => void;
  onSeoImageUrlChange: (value: string) => void;
  onToggleSeoPanel: () => void;
}

export const BlogPostEditorSidebar: React.FC<BlogPostEditorSidebarProps> = ({
  featuredImageUrl,
  featuredImageCrop,
  authorName,
  slug,
  postStatus,
  tags,
  newTag,
  seoTitle,
  seoDescription,
  seoImageUrl,
  title,
  excerpt,
  showSeoPanel,
  isUploadingImage,
  siteId,
  onFeaturedImageUpload: _onFeaturedImageUpload,
  onFeaturedImageUrlChange,
  onRemoveFeaturedImage,
  onFeaturedImageCropChange,
  onAuthorNameChange,
  onSlugChange,
  onNewTagChange,
  onAddTag,
  onRemoveTag,
  onSeoTitleChange,
  onSeoDescriptionChange,
  onSeoImageUrlChange,
  onToggleSeoPanel,
}) => {
  const [showCropModal, setShowCropModal] = useState(false);
  const [showMediaUploadModal, setShowMediaUploadModal] = useState(false);
  const cropPreview = getCroppedImageRenderProps(featuredImageCrop as CropData | undefined);
  const hasEffects = cropPreview.hasCrop;

  // Helper to generate slug from title
  const generateSlugFromTitle = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 80)
      .replace(/^-|-$/g, '');
  };

  // Show placeholder slug based on title if custom slug is empty
  const displaySlug = slug || generateSlugFromTitle(title);

  const handleMediaUploaded = (mediaUrl: string, cloudfrontUrl?: string) => {
    // Prefer CloudFront URL for better performance
    const urlToUse = cloudfrontUrl || mediaUrl;
    onFeaturedImageUrlChange(urlToUse);
    setShowMediaUploadModal(false);
  };

  const handleCropApply = (cropData: CropData, _croppedAreaPixels: Area) => {
    onFeaturedImageCropChange(cropData);
    setShowCropModal(false);
  };
  return (
    <div className="space-y-6">
      {/* Featured Image */}
      <div className="card p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
          <Image size={16} />
          <span>Featured Image</span>
        </h3>

        {featuredImageUrl ? (
          <div className="space-y-3">
            <div
              className={`relative overflow-hidden rounded-lg ${hasEffects && cropPreview.aspectRatio ? 'w-full' : 'h-40'}`}
              style={
                hasEffects && cropPreview.aspectRatio
                  ? { aspectRatio: cropPreview.aspectRatio }
                  : undefined
              }
            >
              <img
                src={featuredImageUrl}
                alt="Featured"
                className={hasEffects ? '' : 'w-full h-full object-cover'}
                style={hasEffects ? cropPreview.imageStyle : undefined}
              />
              {/* Crop/effects indicator badge */}
              {hasEffects && (
                <div className="absolute top-2 left-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center shadow-sm z-10">
                  <Crop size={14} className="text-white" />
                </div>
              )}
              <button
                onClick={onRemoveFeaturedImage}
                className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
              >
                <X size={14} />
              </button>
            </div>
            {/* Crop button */}
            <button
              type="button"
              onClick={() => setShowCropModal(true)}
              className={cn(
                'w-full px-3 py-2 text-sm font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors',
                hasEffects
                  ? 'border-primary-500 text-primary-700 bg-primary-50 hover:bg-primary-100 border'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 border'
              )}
            >
              <Crop size={16} />
              <span>{hasEffects ? 'Edit Crop / Effects' : 'Crop / Add Effects'}</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowMediaUploadModal(true)}
            disabled={isUploadingImage}
            className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 transition-colors"
          >
            {isUploadingImage ? (
              <div className="w-6 h-6 spinner"></div>
            ) : (
              <>
                <Upload className="h-10 w-10 text-gray-400" />
                <span className="mt-2 text-sm text-gray-500">Upload or select from library</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Author */}
      <div className="card p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Author Name
          <span className="font-normal text-gray-500 ml-1">(optional)</span>
        </label>
        <input
          type="text"
          value={authorName}
          onChange={e => onAuthorNameChange(e.target.value)}
          placeholder="Author name"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* URL Slug */}
      <div className="card p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL Slug
          <span className="font-normal text-gray-500 ml-1">(for SEO)</span>
        </label>
        <input
          type="text"
          value={slug}
          onChange={e => onSlugChange(e.target.value)}
          disabled={postStatus === 'published'}
          placeholder={generateSlugFromTitle(title) || 'url-slug'}
          className={cn(
            'w-full border rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            postStatus === 'published'
              ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-200'
              : 'border-gray-300'
          )}
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Post URL:{' '}
          <span className="font-mono text-primary-600">/blog/{displaySlug || 'url-slug'}</span>
        </p>
        {postStatus === 'published' ? (
          <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
            <span>⚠️</span>
            <span>URL cannot be changed after publishing to prevent broken links.</span>
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-500">
            Leave empty to auto-generate from title. Only letters, numbers, and hyphens allowed.
          </p>
        )}
      </div>

      {/* Tags */}
      <div className="card p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
          <Tag size={16} />
          <span>Tags</span>
        </h3>

        <div className="flex flex-wrap gap-2 mb-3">
          {tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-gray-100 text-gray-700"
            >
              {tag}
              <button
                onClick={() => onRemoveTag(tag)}
                className="ml-1 text-gray-400 hover:text-red-600"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>

        <div className="flex space-x-2">
          <input
            type="text"
            value={newTag}
            onChange={e => onNewTagChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddTag();
              }
            }}
            placeholder="Add tag"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            onClick={onAddTag}
            disabled={!newTag.trim()}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {/* SEO Settings */}
      <div className="card p-4">
        <button
          onClick={onToggleSeoPanel}
          className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
        >
          <span>SEO Settings</span>
          <span className={cn('transform transition-transform', showSeoPanel ? 'rotate-180' : '')}>
            ▼
          </span>
        </button>

        {showSeoPanel && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="flex items-center text-sm text-gray-600 mb-1">
                SEO Title
                <span
                  className={cn(
                    'ml-2 text-xs px-1.5 py-0.5 rounded font-medium',
                    seoTitle ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                  )}
                >
                  {seoTitle ? 'Custom' : 'Auto'}
                </span>
              </label>
              <input
                type="text"
                value={seoTitle}
                onChange={e => onSeoTitleChange(e.target.value)}
                placeholder="Enter custom SEO title"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {!seoTitle && title ? (
                <p className="mt-1 text-xs text-gray-500">
                  Using post title by default:{' '}
                  <span className="font-medium">&ldquo;{title}&rdquo;</span>
                  <br />
                  Leave blank to keep using the default.
                </p>
              ) : !seoTitle ? (
                <p className="mt-1 text-xs text-gray-500">Will use the post title if left blank.</p>
              ) : null}
            </div>
            <div>
              <label className="flex items-center text-sm text-gray-600 mb-1">
                SEO Description
                <span
                  className={cn(
                    'ml-2 text-xs px-1.5 py-0.5 rounded font-medium',
                    seoDescription ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                  )}
                >
                  {seoDescription ? 'Custom' : 'Auto'}
                </span>
              </label>
              <textarea
                value={seoDescription}
                onChange={e => onSeoDescriptionChange(e.target.value)}
                placeholder="Enter custom meta description"
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {!seoDescription && excerpt ? (
                <p className="mt-1 text-xs text-gray-500">
                  Using excerpt by default:{' '}
                  <span className="font-medium">
                    &ldquo;{excerpt.length > 120 ? excerpt.slice(0, 120) + '…' : excerpt}&rdquo;
                  </span>
                  <br />
                  Leave blank to keep using the default.
                </p>
              ) : !seoDescription ? (
                <p className="mt-1 text-xs text-gray-500">
                  Will use the post excerpt if left blank.
                </p>
              ) : null}
            </div>
            {siteId ? (
              <ImageUrlWithUpload
                value={seoImageUrl}
                onChange={onSeoImageUrlChange}
                siteId={siteId}
                label="Social Image"
                placeholder="Enter social image URL"
                recommendedSize="1200x630px"
              />
            ) : (
              <div>
                <label className="block text-sm text-gray-600 mb-1">Social Image URL</label>
                <input
                  type="url"
                  value={seoImageUrl}
                  onChange={e => onSeoImageUrlChange(e.target.value)}
                  placeholder="Enter social image URL"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
            {!seoImageUrl && featuredImageUrl && (
              <p className="mt--2 text-xs text-gray-500">
                Social image defaults to the featured image if left blank.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Crop Modal */}
      {featuredImageUrl && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={() => setShowCropModal(false)}
          imageUrl={featuredImageUrl}
          initialCrop={featuredImageCrop as CropData | undefined}
          onApply={handleCropApply}
        />
      )}

      {/* Media Upload Modal for Featured Image */}
      {siteId && (
        <MediaUploadModal
          isOpen={showMediaUploadModal}
          onClose={() => setShowMediaUploadModal(false)}
          siteId={siteId}
          mediaType="image"
          componentType="blog"
          assetType="image"
          currentMediaUrl={featuredImageUrl}
          onMediaUploaded={handleMediaUploaded}
        />
      )}
    </div>
  );
};
